import React, { useEffect, useState, ReactNode } from 'react';
import { DataGridPro, GridColDef, GridToolbar  } from '@mui/x-data-grid-pro';
import { useTheme, Box, Typography, Paper, Container, Stack, CircularProgress, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useGlueClient } from './../../hooks/useGlueClient';
import { useS3Manager } from './../../hooks/useS3Manager';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './../../context/auth-provider';
import { AuthChallange } from './../../data/enums';
import { getDatetimeDisplayString } from '../../utils/util';

const SILVER_CORE_SCHEMA = 'ama_silver_core';
interface ConditionalWrapperProps {
  condition: boolean;
  wrapper: (children: ReactNode) => JSX.Element;
  children: ReactNode;
}

/* eslint-disable react/prop-types */
export default function SchemaView({ isWrapperVisible = true }) {
  
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { getTableDetails } = useGlueClient();
  const { isS3initialized, downloadJsonFile } = useS3Manager({ bucketName: process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET! }); 
  const [tableDetails, setTableDetails] = useState<IGlueCatalogTable | null>(null);
  const [ isLoading, setIsLoading ] = useState<boolean>(false); 

  useEffect(() => {

    // Ensure users authenticated (this view does not load into standard dashboard template)
    if (!user) navigate('/login');

    // Parse required qs params
    const params = new URLSearchParams(location.search);
    const db = params.get('db');
    const table = params.get('table');
    if (!db || !table) return;

    const getData = async () => {
      
      // Show loading interstitial
      setIsLoading(true);
      
      // Get Glue catalog table
      const tableDetails = await getTableDetails(db, table);

      // If this is Silver core schema we need to decorate with supplemental schema data
      if (table === SILVER_CORE_SCHEMA) {

        const supplementalSchemaKey = `${process.env.REACT_APP_AWS_S3_DATA_LAKE_ROOT_PATH}/${process.env.REACT_APP_SILVER_CORE_SUPPLEMENTAL_PATH}`;
        const supplementalSchemaStr = await downloadJsonFile(supplementalSchemaKey);
        const supplementalSchemaItems = JSON.parse(supplementalSchemaStr);
  
        tableDetails?.columns?.forEach((column) => {
          const supplementalItem = supplementalSchemaItems.find((item: any) => item.Name === column.name);
          if (supplementalItem) {
            column.categories = supplementalItem.Categories;
            column.comment = supplementalItem.Comment;
          }
        });
      }

      // Update property
      setTableDetails(tableDetails);      
      
      // Hide loading interstitial
      setIsLoading(false);
    };

    getData();

  }, [location.search]);

  const cellStyle = {
    fontSize: '12px',
    overflowX: 'hidden',
    border: `solid 1px ${theme.palette.grey[100]}`
  };

  const parseDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return getDatetimeDisplayString(date);
  }

  const schemaItemColumns: Array<GridColDef> = [  
    { 
      field: 'name', type: 'string',
      headerName: 'Attribute', flex: .2, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'type', type: 'string',
      headerName: 'Type', flex: .1, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'categories', type: 'string',
      headerName: 'Categories', flex: .4, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'comment', type: 'string',
      headerName: 'Comments', flex: .3, 
      resizable: true, sortable: true
    }
  ];

  const wrapData = (results: Array<IGlueCatalogColumn>) => {
    let idCounter = 1;
    return results.map((jsonObject) => {
      const newObj = { ...jsonObject, id: idCounter };
      idCounter++;
      return newObj;
    });
  };

  const CustomFooter = (count: number | undefined) => {
    return (
      <Stack sx={{ height: 30, backgroundColor: 'white' }} justifyContent="center" alignItems="center">
        Records: {count}
      </Stack>
    );
  };
  
  const ConditionalWrapper = ({ condition, wrapper, children }: ConditionalWrapperProps): any => {
    return condition ? wrapper(children) : children;
  };

  return (
    <ConditionalWrapper      
      condition={isWrapperVisible}
      wrapper={(children) => 
        <Box
          id="schema-optional-wrapper"
          sx={{ 
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',        
            alignItems: 'center', justifyContent: 'flex-start',
            backgroundColor: theme.palette.grey[100],
          }}>
          {children}
        </Box>
      }>
      
      <Container
        id="schema-content-root"
        maxWidth={isWrapperVisible ? 'lg' : false} 
        sx={{
          display: 'flex',
          mt: isWrapperVisible ? 2 : 0,
          width: '100%',
          height: isWrapperVisible ? '95%' : '90%',
          padding: '0 !important'
        }}>

        {/* Loading overlay */}
        {isLoading &&
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.6)',
              zIndex: 999,
            }}>
            <CircularProgress />
          </div>
        }

        <Box
          component={isWrapperVisible ? Paper : 'div'}
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            p: isWrapperVisible ? 2 : 0,
            m: 0,
            width: '100%'
          }}>

          {/* Title */}
          <Box sx={{ width: '100%', display: isWrapperVisible ? 'block' : 'none' }}>
            <Typography component="h2" variant="h6" gutterBottom color='primary' sx={{ pb: 2 }}>
              AWS Glue Catalog Table
            </Typography>
          </Box>

          {/* Schema props */}
          <Box sx={{ width: '100%', }}>
            <TableContainer component={'div'} sx={{ backgroundColor: 'white' }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ ...cellStyle, width: '10%' }}><strong>Database</strong></TableCell>
                    <TableCell sx={{ ...cellStyle, width: '15%' }}>{tableDetails?.databaseName}</TableCell>
                    <TableCell sx={{ ...cellStyle, width: '10%' }}><strong>Created</strong></TableCell>
                    <TableCell sx={{ ...cellStyle, width: '15%' }}>{parseDate(tableDetails?.createTime)}</TableCell>
                    <TableCell sx={{ ...cellStyle, width: '10%' }}><strong>Classification</strong></TableCell>
                    <TableCell sx={{ ...cellStyle, width: '15%' }}>{tableDetails?.classification}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...cellStyle }}><strong>Table</strong></TableCell>
                    <TableCell sx={{ ...cellStyle }}>{tableDetails?.tableName}</TableCell>
                    <TableCell sx={{ ...cellStyle }}><strong>Record Count</strong></TableCell>
                    <TableCell sx={{ ...cellStyle }}>{tableDetails?.recordCount}</TableCell>
                    <TableCell sx={{ ...cellStyle }}><strong>Delimiter</strong></TableCell>
                    <TableCell sx={{ ...cellStyle }}>{tableDetails?.delimiter}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Schema attributes - searchable grid */}
          <Box sx={{ width: '100%', flexGrow: 1, overflow: 'hidden', my: 2 }}> 
            {tableDetails && tableDetails.columns &&                     
              <DataGridPro
                columns={schemaItemColumns}
                rows={wrapData(tableDetails.columns)}
                rowThreshold={0}      
                disableDensitySelector={true}
                disableColumnResize={false}          
                disableColumnSelector={true}
                disableRowSelectionOnClick={true}
                disableMultipleRowSelection={true}  
                slots={{ 
                  toolbar: GridToolbar,
                  footer: () => CustomFooter(tableDetails.columns.length)
                }}   
                slotProps={{
                  toolbar: {
                    sx: { backgroundColor: 'white' },
                    showQuickFilter: true,            
                    printOptions: { disableToolbarButton: true },
                    csvOptions: {disableToolbarButton: true}           
                  }}}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                }           
                sx={{
                  mt: 0,
                  height: '100%',
                  '& .MuiDataGrid-root': {
                    color: theme => theme.palette.text.primary,
                    fontSize: '0.8rem'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.background.paper,
                    color: theme => theme.palette.text.primary,
                    fontSize: '.8rem'
                  },
                  '& .MuiDataGrid-cell': {
                    backgroundColor: 'white',
                    color: theme => theme.palette.text.primary,
                    fontSize: '0.8rem'
                  }
                }}
              />
            }
          </Box>

        </Box>
      </Container>
    </ConditionalWrapper>
  );
}




